import PropTypes from 'prop-types';
import React from 'react';
import Select from 'react-select';
import styled, { useTheme } from 'styled-components';

const SelectLabel = styled.label`
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.text?.black || '#000000'};
  font-weight: 500;
  margin-bottom: 6px;
  font-size: 14px;
  display: block;
`;

/**
 * ThemedSelect - A theme-aware select component using react-select
 *
 * Supports both light and dark themes with proper styling
 */
const ThemedSelect = ({
  options,
  value,
  onChange,
  onBlur,
  label,
  placeholder = 'Select...',
  isMulti = false,
  isSearchable = true,
  isClearable = false,
  isDisabled = false,
  closeMenuOnSelect = true,
  customStyles = {},
  menuPortalTarget,
  maxMenuHeight,
  ...props
}) => {
  const theme = useTheme();
  const isDark = theme?.name === 'dark';

  // Theme-aware colors
  const colors = {
    // Backgrounds
    controlBg: isDark ? 'rgba(255, 255, 255, 0.05)' : '#ffffff',
    controlBgHover: isDark ? 'rgba(255, 255, 255, 0.08)' : '#ffffff',
    controlBgDisabled: isDark ? 'rgba(255, 255, 255, 0.02)' : '#f3f4f6',
    menuBg: isDark ? '#1e293b' : '#ffffff',
    optionBg: isDark ? '#1e293b' : '#ffffff',
    optionBgFocused: isDark
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)',
    optionBgSelected: isDark
      ? 'rgba(250, 70, 22, 0.25)'
      : 'rgba(250, 70, 22, 0.12)',
    multiBg: isDark ? 'rgba(250, 70, 22, 0.2)' : 'rgba(250, 70, 22, 0.1)',

    // Borders
    border: isDark ? 'rgba(255, 255, 255, 0.15)' : '#d1d5db',
    borderHover: isDark ? 'rgba(255, 255, 255, 0.25)' : '#9ca3af',
    borderFocused: theme?.colors?.primary || '#FA4616',

    // Text
    text: isDark
      ? theme?.colors?.textGray?.primary || '#ffffff'
      : theme?.colors?.text?.black || '#1f2937',
    textMuted: isDark
      ? theme?.colors?.textGray?.secondary || '#94a3b8'
      : '#6b7280',
    textDisabled: isDark ? 'rgba(255, 255, 255, 0.3)' : '#9ca3af',

    // Primary
    primary: theme?.colors?.primary || '#FA4616',
  };

  const themedStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: 46,
      backgroundColor: state.isDisabled
        ? colors.controlBgDisabled
        : colors.controlBg,
      borderColor: state.isFocused
        ? colors.borderFocused
        : state.isDisabled
        ? colors.border
        : colors.border,
      borderRadius: 8,
      boxShadow: state.isFocused ? `0 0 0 1px ${colors.borderFocused}` : 'none',
      '&:hover': {
        borderColor: state.isFocused
          ? colors.borderFocused
          : colors.borderHover,
        backgroundColor: state.isDisabled
          ? colors.controlBgDisabled
          : colors.controlBgHover,
      },
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '2px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: colors.textMuted,
      fontSize: 14,
    }),
    singleValue: (provided, state) => ({
      ...provided,
      color: state.isDisabled ? colors.textDisabled : colors.text,
      fontSize: 14,
    }),
    input: (provided) => ({
      ...provided,
      color: colors.text,
      fontSize: 14,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: colors.menuBg,
      border: `1px solid ${colors.border}`,
      borderRadius: 8,
      boxShadow: isDark
        ? '0 10px 40px rgba(0, 0, 0, 0.5)'
        : '0 10px 40px rgba(0, 0, 0, 0.1)',
      zIndex: 9999,
      overflow: 'hidden',
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 4,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? colors.optionBgSelected
        : state.isFocused
        ? colors.optionBgFocused
        : colors.optionBg,
      color: state.isSelected ? colors.primary : colors.text,
      fontSize: 14,
      padding: '10px 12px',
      borderRadius: 4,
      cursor: 'pointer',
      '&:active': {
        backgroundColor: colors.optionBgSelected,
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? colors.primary : colors.textMuted,
      padding: '8px 12px',
      '&:hover': {
        color: colors.primary,
      },
      svg: {
        width: 18,
        height: 18,
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: colors.textMuted,
      padding: '8px 4px',
      '&:hover': {
        color: colors.primary,
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: colors.multiBg,
      borderRadius: 4,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: colors.primary,
      fontSize: 13,
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: colors.primary,
      '&:hover': {
        backgroundColor: 'rgba(250, 70, 22, 0.3)',
        color: colors.primary,
      },
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      color: colors.textMuted,
      fontSize: 14,
    }),
    loadingMessage: (provided) => ({
      ...provided,
      color: colors.textMuted,
      fontSize: 14,
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  const handleChange = (val) => {
    onChange(val);
  };

  const handleBlur = () => {
    if (onBlur) onBlur(true);
  };

  return (
    <>
      {label && <SelectLabel>{label}</SelectLabel>}
      <Select
        {...props}
        options={options}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        isMulti={isMulti}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={isDisabled}
        closeMenuOnSelect={closeMenuOnSelect}
        styles={{ ...themedStyles, ...customStyles }}
        menuPortalTarget={menuPortalTarget}
        maxMenuHeight={maxMenuHeight}
      />
    </>
  );
};

ThemedSelect.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ).isRequired,
  value: PropTypes.oneOfType([
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string,
      })
    ),
  ]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isMulti: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isClearable: PropTypes.bool,
  isDisabled: PropTypes.bool,
  closeMenuOnSelect: PropTypes.bool,
  customStyles: PropTypes.object,
  menuPortalTarget: PropTypes.any,
  maxMenuHeight: PropTypes.number,
};

export default ThemedSelect;
